import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// Pages
import HomePage from "./pages/HomePage";
import AbbigliamentoPage from "./pages/AbbigliamentoPage";
import MagliettePage from "./pages/MagliettePage";
import FelpePage from "./pages/FelpePage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import ProfilePage from "./pages/ProfilePage";
import OrderDetailPage from "./pages/OrderDetailPage";
import ChiSiamoPage from "./pages/ChiSiamoPage";
import ContattiPage from "./pages/ContattiPage";
import AuthPage from "./pages/AuthPage";
import AdminPage from "./pages/AdminPage";

// Components
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Cart Context
export const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-black border-t-accent-pink animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-black border-t-accent-pink animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// App Router Component
const AppRouter = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/abbigliamento" element={<AbbigliamentoPage />} />
          <Route path="/magliette" element={<MagliettePage />} />
          <Route path="/felpe" element={<FelpePage />} />
          <Route path="/prodotto/:productId" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/profilo" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } />
          <Route path="/ordine/:orderId" element={
            <ProtectedRoute>
              <OrderDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/chi-siamo" element={<ChiSiamoPage />} />
          <Route path="/contatti" element={<ContattiPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartLoading, setCartLoading] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fetch cart when user changes
  useEffect(() => {
    const fetchCart = async () => {
      if (!user) {
        setCart({ items: [], total: 0 });
        return;
      }

      setCartLoading(true);
      try {
        const response = await axios.get(`${API}/cart`, { withCredentials: true });
        setCart(response.data);
      } catch (err) {
        console.error("Error fetching cart:", err);
      } finally {
        setCartLoading(false);
      }
    };

    fetchCart();
  }, [user]);

  const login = () => {
    window.location.href = "/login";
  };

  const loginWithGoogle = () => {
    const origin = window.location.origin;
    window.location.href = `${API}/auth/google/login?origin=${encodeURIComponent(origin)}`;
  };

  const loginWithEmail = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
  };

  const registerWithEmail = async (name, email, password) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    setUser(response.data);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setCart({ items: [], total: 0 });
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const refreshCart = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(response.data);
    } catch (err) {
      console.error("Error refreshing cart:", err);
    }
  };

  const addToCart = async (productId, size, color, quantity = 1) => {
    if (!user) {
      login();
      return false;
    }

    try {
      await axios.post(`${API}/cart/add`, 
        { product_id: productId, size, color, quantity },
        { withCredentials: true }
      );
      await refreshCart();
      return true;
    } catch (err) {
      console.error("Error adding to cart:", err);
      return false;
    }
  };

  const updateCartItem = async (productId, size, color, quantity) => {
    try {
      await axios.put(`${API}/cart/update`,
        { product_id: productId, size, color, quantity },
        { withCredentials: true }
      );
      await refreshCart();
    } catch (err) {
      console.error("Error updating cart:", err);
    }
  };

  const removeFromCart = async (productId, size, color) => {
    try {
      await axios.delete(`${API}/cart/remove`, {
        params: { product_id: productId, size, color },
        withCredentials: true
      });
      await refreshCart();
    } catch (err) {
      console.error("Error removing from cart:", err);
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, { withCredentials: true });
      setCart({ items: [], total: 0 });
    } catch (err) {
      console.error("Error clearing cart:", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, loginWithEmail, registerWithEmail, logout }}>
      <CartContext.Provider value={{ 
        cart, 
        cartLoading, 
        addToCart, 
        updateCartItem, 
        removeFromCart, 
        clearCart, 
        refreshCart 
      }}>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </CartContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
